import React, { useState, useEffect } from "react";

const FeedStorage = () => {
  const [storageData, setStorageData] = useState({
    bagsRemoved: 12,
    bagsInStorage: 148,
    totalCapacity: 200,
    storageOpenDuration: 24,
    unauthorizedAttempts: 0,
    lastBagRemoval: "14:32",
    lastPersonEntry: "14:28",
    roomTemperature: 26.5,
    humidity: 68,
  });

  const [movements, setMovements] = useState([
    {
      id: 1,
      timestamp: "14:32:15",
      type: "bag_removed",
      bags: 2,
      person: "Vikram Patel",
      zone: "Storage Room A",
      confidence: 98,
    },
    {
      id: 2,
      timestamp: "14:28:42",
      type: "person_entry",
      person: "Rajesh Kumar",
      zone: "Storage Room A",
      confidence: 99,
    },
    {
      id: 3,
      timestamp: "14:15:03",
      type: "bag_removed",
      bags: 3,
      person: "Aisha Desai",
      zone: "Storage Room B",
      confidence: 97,
    },
    {
      id: 4,
      timestamp: "13:52:18",
      type: "person_entry",
      person: "Priya Sharma",
      zone: "Storage Room A",
      confidence: 95,
    },
    {
      id: 5,
      timestamp: "13:45:09",
      type: "unauthorized_access",
      person: "Unknown",
      zone: "Restricted Area",
      confidence: 87,
      status: "flagged",
    },
  ]);

  const [selectedMovement, setSelectedMovement] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setStorageData((prev) => {
        const randomBags = Math.random() > 0.85 ? 1 : 0;
        const tempVariance = (Math.random() - 0.5) * 0.3;
        const humidityVariance = (Math.random() - 0.5) * 1;

        return {
          ...prev,
          bagsRemoved: prev.bagsRemoved + randomBags,
          bagsInStorage: Math.max(0, prev.bagsInStorage - randomBags),
          storageOpenDuration: prev.storageOpenDuration + 0.25,
          roomTemperature:
            Math.round((prev.roomTemperature + tempVariance) * 10) / 10,
          humidity: Math.max(
            40,
            Math.min(90, Math.round((prev.humidity + humidityVariance) * 10) / 10)
          ),
        };
      });

      if (Math.random() > 0.92) {
        setMovements((prev) => [
          {
            id: Math.max(...prev.map((m) => m.id), 0) + 1,
            timestamp: new Date().toLocaleTimeString(),
            type: Math.random() > 0.5 ? "bag_removed" : "person_entry",
            bags: Math.random() > 0.5 ? Math.floor(Math.random() * 3) + 1 : undefined,
            person:
              ["Vikram Patel", "Rajesh Kumar", "Aisha Desai", "Priya Sharma"][
                Math.floor(Math.random() * 4)
              ],
            zone:
              ["Storage Room A", "Storage Room B", "Feed Processing Unit"][
                Math.floor(Math.random() * 3)
              ],
            confidence: 92 + Math.floor(Math.random() * 8),
          },
          ...prev.slice(0, 4),
        ]);
      }

      setLastUpdate(new Date());
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const utilizationPercent = Math.round(
    ((storageData.totalCapacity - storageData.bagsInStorage) /
      storageData.totalCapacity) *
      100
  );

  const getMovementTypeColor = (type) => {
    switch (type) {
      case "bag_removed":
        return "bg-blue-50 border-blue-200 text-blue-900";
      case "person_entry":
        return "bg-emerald-50 border-emerald-200 text-emerald-900";
      case "unauthorized_access":
        return "bg-red-50 border-red-200 text-red-900";
      default:
        return "bg-gray-50 border-gray-200 text-gray-900";
    }
  };

  const getMovementBadge = (type, status) => {
    switch (type) {
      case "bag_removed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
            📦 Bag Removed
          </span>
        );
      case "person_entry":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
            👤 Entry
          </span>
        );
      case "unauthorized_access":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
            ⚠ Unauthorized
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-5 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Feed Storage Monitoring</h2>
        <p className="text-sm text-gray-500 mt-1">
          Real-time tracking of storage access and inventory
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 py-5 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Bags Removed
          </span>
          <span className="text-2xl font-bold text-blue-600 mt-1">
            {Math.round(storageData.bagsRemoved)}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            In Storage
          </span>
          <span className="text-2xl font-bold text-emerald-600 mt-1">
            {Math.round(storageData.bagsInStorage)}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Utilization
          </span>
          <span className="text-2xl font-bold text-purple-600 mt-1">
            {utilizationPercent}%
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Unauthorized
          </span>
          <span className="text-2xl font-bold text-red-600 mt-1">
            {storageData.unauthorizedAttempts}
          </span>
        </div>
      </div>

      <div className="px-6 py-5 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Storage Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="flex flex-col">
            <span className="text-xs text-gray-600 mb-1">Room Temperature</span>
            <span className="text-lg font-semibold text-gray-900">
              {storageData.roomTemperature}°C
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-gray-600 mb-1">Humidity</span>
            <span className="text-lg font-semibold text-gray-900">
              {storageData.humidity}%
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-gray-600 mb-1">Open Duration</span>
            <span className="text-lg font-semibold text-gray-900">
              {Math.round(storageData.storageOpenDuration)} min
            </span>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-medium text-gray-600">Storage Capacity</span>
            <span className="text-xs font-medium text-gray-600">
              {Math.round(storageData.bagsInStorage)} / {storageData.totalCapacity}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{
                width: `${(storageData.bagsInStorage / storageData.totalCapacity) * 100}%`,
              }}
            ></div>
          </div>
        </div>
      </div>

      <div className="px-6 py-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {movements.map((movement) => (
            <div
              key={movement.id}
              onClick={() =>
                setSelectedMovement(
                  selectedMovement?.id === movement.id ? null : movement
                )
              }
              className={`border rounded-lg p-4 cursor-pointer transition-all ${getMovementTypeColor(
                movement.type
              )} ${
                selectedMovement?.id === movement.id
                  ? "ring-2 ring-offset-0 ring-blue-400"
                  : "hover:shadow-md"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-sm">
                    {movement.type === "bag_removed"
                      ? `${movement.bags} Bag${movement.bags > 1 ? "s" : ""} Removed`
                      : movement.type === "person_entry"
                      ? "Person Entry"
                      : "Unauthorized Access"}
                  </p>
                  <p className="text-xs opacity-75 mt-1">{movement.timestamp}</p>
                </div>
                {getMovementBadge(movement.type, movement.status)}
              </div>

              <div className="space-y-2 text-sm">
                {movement.person && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Person</span>
                    <span className="font-semibold text-xs">{movement.person}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Zone</span>
                  <span className="font-semibold text-xs">{movement.zone}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Confidence</span>
                  <span className="font-mono font-semibold text-xs">
                    {movement.confidence}%
                  </span>
                </div>
              </div>

              {selectedMovement?.id === movement.id && (
                <div className="mt-4 pt-4 border-t border-current border-opacity-20 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Detection Method</span>
                    <span className="font-semibold text-xs">
                      {movement.type === "bag_removed"
                        ? "Object Movement"
                        : "Person Detection"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Frame ID</span>
                    <span className="font-mono text-xs">
                      #FRAME-{Math.floor(Math.random() * 10000)
                        .toString()
                        .padStart(5, "0")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Model Version</span>
                    <span className="font-mono text-xs">v4.2.1</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
        Last updated: {lastUpdate.toLocaleTimeString()} • Detection confidence: 97%
      </div>
    </div>
  );
};

export default FeedStorage;